import { useEffect, useRef, useCallback } from 'react';
import { logoutUser } from '../util/util';

/**
 * Auto-logout hook - logs user out after specified minutes of inactivity
 * @param {number} minutes - Minutes of inactivity before logout (default: 5)
 * @param {boolean} isEnabled - Whether auto-logout is enabled (default: true)
 */
export const useAutoLogout = (minutes = 5, isEnabled = true) => {
  const timerRef = useRef(null);
  const timeoutMs = minutes * 60 * 1000;

  const logout = useCallback(() => {
    logoutUser();
  }, []);

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(logout, timeoutMs);
  }, [logout, timeoutMs]);

  useEffect(() => {
    if (!isEnabled) return;

    // Events to track user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown',
    ];

    const handleActivity = () => {
      resetTimer();
    };

    // Add event listeners
    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    // Start the timer
    resetTimer();

    // Cleanup
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [isEnabled, resetTimer]);
};

export default useAutoLogout;
